import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion, type Variants, type Transition } from 'framer-motion';
import { getJob, getSkin, searchProducts, productClick } from '../api/endpoints';
import { getCorrectedJob, postCorrectJob, type CorrectedJobDTO, type PostCorrectJobPayload } from '../api/endpoints';
import { AuthImage } from '../components/AuthImage';
import Loading from '../components/Loading';
import { iconForConcern } from '../components/icons/SkinIcons';
import { postJobView } from '../api/telemetry';
import AIDisclaimerFooter from '../components/AIDisclaimerFooter';
import { CONCERN_LABELS, concernLabel, isSurfacedConcern } from '../utils/conditionLabels';
import type { Job, SkinDetail, ProductSearchResultItem } from '../types/api';
import { useReportExport } from '../reports/useReportExport';

type CorrectedJob = CorrectedJobDTO['corrected_job'];

type ConcernKey =
  | 'skin_darkcircle'
  | 'skin_skinredness'
  | 'skin_vascular'
  | 'skin_wrinkle'
  | 'skin_acne';

type SkinTypeKey = 'skin_oily' | 'skin_dry' | 'skin_normal';

type LevelKey = 'none' | 'mild' | 'moderate' | 'high';

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function pct(v: number): number {
  return Math.round(v * 100);
}

function truncated(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + '…' : s;
}

function capFirst(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

function barVariant(name: string): { dot: string; bar: string } {
  const n = name.toLowerCase();
  if (n.includes('red')) return { dot: 'concern-dot--coral', bar: 'result-bar--coral' };
  if (n.includes('wrinkl')) return { dot: 'concern-dot--deep', bar: 'result-bar--deep' };
  return { dot: 'concern-dot--teal', bar: '' };
}

function keyForConcernName(name: string): ConcernKey | null {
  const n = name.toLowerCase();
  if (n.includes('dark')) return 'skin_darkcircle';
  if (n.includes('red')) return 'skin_skinredness';
  if (n.includes('vasc')) return 'skin_vascular';
  if (n.includes('wrinkl')) return 'skin_wrinkle';
  if (n.includes('acne')) return 'skin_acne';
  return null;
}

function jobValue(job: Job | undefined, k: ConcernKey): number {
  return clamp01(job?.[k] ?? 0);
}

function findSkinConfidenceForKey(skin: SkinDetail | undefined, k: ConcernKey): number | undefined {
  const list = skin?.concerns ?? [];
  for (const item of list) {
    const mapped = keyForConcernName(item.name);
    if (mapped === k) return clamp01(item.confidence);
  }
  return undefined;
}

function mergeSkinWithCorrected(s: SkinDetail, c: CorrectedJob): SkinDetail {
  const concerns = s.concerns.map((item) => {
    const k = keyForConcernName(item.name);
    if (k) return { ...item, confidence: clamp01(c[k]) };
    return item;
  });
  const trio: { key: SkinTypeKey; label: 'oily' | 'dry' | 'normal' }[] = [
    { key: 'skin_oily', label: 'oily' },
    { key: 'skin_dry', label: 'dry' },
    { key: 'skin_normal', label: 'normal' },
  ];
  const maxK = trio.reduce(
    (best, cur) => (c[cur.key] > c[best.key] ? cur : best),
    trio[0]
  );
  return { ...s, concerns, skin_type: maxK.label };
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function dominantSkinTypeFromDraft(d: CorrectedJob): 'oily' | 'normal' | 'dry' {
  const entries: ['oily' | 'normal' | 'dry', number][] = [
    ['oily', d.skin_oily],
    ['normal', d.skin_normal],
    ['dry', d.skin_dry],
  ];
  return entries.sort((a, b) => b[1] - a[1])[0][0];
}

function applySkinType(d: CorrectedJob, k: 'oily' | 'normal' | 'dry'): CorrectedJob {
  return {
    ...d,
    skin_oily: k === 'oily' ? 1 : 0,
    skin_normal: k === 'normal' ? 1 : 0,
    skin_dry: k === 'dry' ? 1 : 0,
  };
}

function levelKeyFromValue(v: number): LevelKey {
  const levels: { key: LevelKey; v: number }[] = [
    { key: 'none', v: 0 },
    { key: 'mild', v: 0.33 },
    { key: 'moderate', v: 0.66 },
    { key: 'high', v: 1 },
  ];
  return levels.reduce((best, cur) =>
    Math.abs(cur.v - v) < Math.abs(best.v - v) ? cur : best,
    levels[0]
  ).key;
}

function skinTypeBreakdownValue(
  corrected: CorrectedJob | null,
  job: Job,
  key: SkinTypeKey
): number {
  return clamp01(corrected ? corrected[key] : job[key] ?? 0);
}

function isEffectCancelled(cancelledRef: React.RefObject<boolean>): boolean {
  return cancelledRef.current === true;
}

const ALL_CONCERNS: { name: string; key: ConcernKey }[] = [
  { name: CONCERN_LABELS.skin_darkcircle, key: 'skin_darkcircle' },
  { name: CONCERN_LABELS.skin_skinredness, key: 'skin_skinredness' },
  { name: CONCERN_LABELS.skin_vascular, key: 'skin_vascular' },
  { name: CONCERN_LABELS.skin_wrinkle, key: 'skin_wrinkle' },
  { name: CONCERN_LABELS.skin_acne, key: 'skin_acne' },
];

export default function AnalysisResultPage(): React.JSX.Element {
  const { exporting, exportReport, errorToast } = useReportExport();
  const { id } = useParams<{ id: string }>();

  const [job, setJob] = useState<Job | undefined>();
  const [skin, setSkin] = useState<SkinDetail | undefined>();
  const [rawSkin, setRawSkin] = useState<SkinDetail | undefined>();
  const [corrected, setCorrected] = useState<CorrectedJob | null>(null);

  const [products, setProducts] = useState<Record<string, ProductSearchResultItem[]>>({});
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);
  const [expandedAdvice, setExpandedAdvice] = useState(false);

  const [openRefiner, setOpenRefiner] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'ok' | 'err'>('idle');

  const loadCancelledRef = useRef(false);

  const prefersReduced = useReducedMotion();
  const spring: Transition = prefersReduced
    ? { duration: 0 }
    : { type: 'spring', stiffness: 380, damping: 34, mass: 0.95 };
  const quick: Transition = prefersReduced ? { duration: 0 } : { duration: 0.18 };

  const sectionIn: Variants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: spring },
  };
  const listContainer: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReduced ? 0 : 0.06,
        delayChildren: prefersReduced ? 0 : 0.05,
      },
    },
  };
  const listItem: Variants = {
    hidden: { opacity: 0, y: 4 },
    visible: { opacity: 1, y: 0, transition: spring },
  };

  useEffect(() => {
    if (!id) return;
    loadCancelledRef.current = false;
    void (async () => {
      const [j, s] = await Promise.all([getJob(id), getSkin(id)]);
      if (isEffectCancelled(loadCancelledRef)) return;
      setJob(j);
      setRawSkin(s);
      setSkin(s);
      void postJobView(id);
      try {
        const cj = await getCorrectedJob(id);
        if (isEffectCancelled(loadCancelledRef)) return;
        const c = cj.corrected_job;
        setCorrected(c);
        setSkin(mergeSkinWithCorrected(s, c));
        const s2 = await getSkin(id);
        if (isEffectCancelled(loadCancelledRef)) return;
        setSkin(s2);
        setProducts({});
        setLoadingMap({});
      } catch {
        // no correction yet
      }
    })();
    return () => { loadCancelledRef.current = true; };
  }, [id]);

  const [draft, setDraft] = useState<CorrectedJob | null>(null);
  useEffect(() => {
    if (!job || !rawSkin || !id) return;
    const base: CorrectedJob = corrected ?? {
      job_id: id,
      skin_oily: clamp01(job.skin_oily ?? 0),
      skin_dry: clamp01(job.skin_dry ?? 0),
      skin_normal: clamp01(job.skin_normal ?? 0),
      skin_darkcircle: clamp01(job.skin_darkcircle ?? 0),
      skin_skinredness: clamp01(job.skin_skinredness ?? 0),
      skin_vascular: clamp01(job.skin_vascular ?? 0),
      skin_wrinkle: clamp01(job.skin_wrinkle ?? 0),
      skin_acne: clamp01(job.skin_acne ?? 0),
    };
    setDraft(base);
  }, [job, rawSkin, corrected, id]);

  const computedConcerns = useMemo(() => {
    return ALL_CONCERNS.map((c) => {
      const correctedVal = corrected ? clamp01(corrected[c.key]) : undefined;
      const skinVal = findSkinConfidenceForKey(skin, c.key);
      const aiVal = jobValue(job, c.key);
      const display = clamp01(correctedVal ?? skinVal ?? aiVal);
      return { name: c.name, key: c.key, value: display, ghost: aiVal };
    });
  }, [skin, corrected, job]);

  const allKeywords = useMemo<string[]>(() => {
    if (!skin) return [];
    const base = skin.base_products;
    const fromConcerns = skin.concerns
      .filter((c) => isSurfacedConcern(c.name))
      .flatMap((c) => c.recommended_products);
    return Array.from(new Set([...base, ...fromConcerns])).filter(Boolean);
  }, [skin]);

  useEffect(() => {
    if (allKeywords.length === 0) return;
    setProducts((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([k]) => allKeywords.includes(k)))
    );
    setLoadingMap((prev) => {
      const next: Record<string, boolean> = { ...prev };
      for (const k of allKeywords) {
        if (!(k in next)) next[k] = false;
      }
      return next;
    });
  }, [allKeywords]);

  const fetchProductsFor = useCallback(
    async (keyword: string) => {
      if (!keyword) return;
      if ((keyword in products && products[keyword].length > 0) || loadingMap[keyword]) return;
      setLoadingMap((m) => ({ ...m, [keyword]: true }));
      try {
        const items = await searchProducts({ product_keywords: keyword, limit: 8 });
        setProducts((p) => ({ ...p, [keyword]: items }));
      } catch {
        setProducts((p) => ({ ...p, [keyword]: [] }));
      } finally {
        setLoadingMap((m) => ({ ...m, [keyword]: false }));
      }
    },
    [products, loadingMap]
  );

  const openKeyword = useCallback(
    (key: string) => {
      setSelectedKeyword(key);
      void fetchProductsFor(key);
    },
    [fetchProductsFor]
  );

  const linkifyText = useCallback(
    (text: string) => {
      if (!text || allKeywords.length === 0) return text;
      const sorted = [...allKeywords].sort((a, b) => b.length - a.length).map(escapeRegExp);
      const re = new RegExp(`(^|[^\\w])((${sorted.join('|')}))(?=$|[^\\w])`, 'gi');
      const out: (string | React.JSX.Element)[] = [];
      let idx = 0;
      text.replace(re, (match: string, before: string, word: string, _capture: string, offset: number) => {
        const end = offset + match.length;
        if (offset > idx) out.push(text.slice(idx, offset));
        if (before) out.push(before);
        const key = allKeywords.find((k) => k.toLowerCase() === word.toLowerCase()) ?? word;
        out.push(
          <button
            key={`${key}-${offset}`}
            type="button"
            style={{
              fontWeight: 600,
              color: 'var(--c-deep-teal)',
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              font: 'inherit',
            }}
            onClick={() => { openKeyword(key); }}
          >
            {word}
          </button>
        );
        idx = end;
        return match;
      });
      if (idx < text.length) out.push(text.slice(idx));
      return out;
    },
    [allKeywords, openKeyword]
  );

  const loading = !id || !job || !skin || !rawSkin;
  if (loading) return <Loading />;

  const overviewText = skin.general_advice;
  const skinType = skin.skin_type.toLowerCase() as 'oily' | 'dry' | 'normal';

  // ---- UI
  return (
    <motion.div initial="hidden" animate="visible" variants={listContainer} className="stack-6">

      {/* ── Header ────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <motion.h1 variants={listItem} className="t-h2" style={{ margin: 0 }}>
          Skin <em className="t-italic">analysis</em> report
        </motion.h1>
        <button
          type="button"
          onClick={() => { void exportReport('base', job.id); }}
          disabled={exporting}
          className="btn btn--ghost btn--sm"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s-2)' }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>download</span>
          {exporting ? 'Preparing…' : 'Export'}
        </button>
        {errorToast}
      </div>

      {/* ── Portrait + probabilities ───────────────────────── */}
      <div className="g-2">

        {/* Portrait card */}
        <motion.section
          variants={sectionIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="card"
          style={{ padding: 'var(--s-4)' }}
        >
          <motion.div
            className="frame"
            style={{ position: 'relative' }}
            initial={{ scale: 0.98, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={spring}
            viewport={{ once: true, amount: 0.3 }}
          >
            <AuthImage
              path={job.image_path}
              alt="Analyzed portrait"
              style={{ width: '100%', height: '280px', objectFit: 'cover', display: 'block' }}
              decoding="async"
            />
            <div style={{ position: 'absolute', top: 'var(--s-3)', right: 'var(--s-3)' }}>
              <span
                className="t-label"
                style={{
                  background: 'var(--c-cream-60)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  padding: '4px 10px',
                  borderRadius: 'var(--r-pill)',
                }}
              >
                Skin type result
              </span>
            </div>
            <motion.div
              style={{ position: 'absolute', bottom: 'var(--s-3)', right: 'var(--s-3)' }}
              initial={{ y: 8, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={spring}
              viewport={{ once: true, amount: 0.5 }}
            >
              <div className={`type-badge type-badge--${skinType}`}>
                <span className="t-label" style={{ color: 'var(--ink)' }}>
                  {skin.skin_type.toUpperCase()}
                </span>
              </div>
            </motion.div>
          </motion.div>

          {/* Refine CTA */}
          <div
            style={{
              marginTop: 'var(--s-4)',
              border: '1px solid var(--rule)',
              borderRadius: 'var(--r-md)',
              padding: 'var(--s-4)',
            }}
          >
            <div className="row-3">
              <div className="concern-dot concern-dot--teal" style={{ flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>tune</span>
              </div>
              <div>
                <div style={{ fontWeight: 500, fontSize: 'var(--fs-small)', color: 'var(--ink)' }}>
                  Adjust your result
                </div>
                <p className="t-small t-mute" style={{ margin: 0 }}>
                  Fine-tune values so recommendations align with how your skin actually feels.
                </p>
              </div>
            </div>
            <div
              style={{
                marginTop: 'var(--s-3)',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--s-2)',
                alignItems: 'center',
              }}
            >
              <motion.button
                type="button"
                whileTap={{ scale: prefersReduced ? 1 : 0.98 }}
                className="btn btn--primary btn--sm"
                onClick={() => { setOpenRefiner((s) => !s); }}
              >
                {openRefiner ? 'Close' : 'Adjust profile'}
              </motion.button>
              {saveState === 'ok' && <span className="t-small t-teal">Saved</span>}
              {saveState === 'err' && <span className="t-small t-coral">Save failed</span>}
            </div>
          </div>

          {/* Inline editor */}
          <AnimatePresence initial={false}>
            {openRefiner && draft && (
              <motion.div
                key="editor"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={quick}
                className="result-editor"
                style={{ marginTop: 'var(--s-3)' }}
              >
                <div className="result-editor__section">
                  <span className="t-label">Skin type</span>
                  <Segmented
                    options={[
                      { key: 'oily',   label: 'Oily'   },
                      { key: 'normal', label: 'Normal' },
                      { key: 'dry',    label: 'Dry'    },
                    ]}
                    value={dominantSkinTypeFromDraft(draft)}
                    onChange={(k) => { setDraft(applySkinType(draft, k)); }}
                  />
                  <p className="t-small t-mute" style={{ margin: 0 }}>
                    Choose the option that best matches how your skin felt in the photo.
                  </p>
                </div>

                <div className="result-editor__section">
                  <span className="t-label">Concern levels</span>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: 'var(--s-3)',
                    }}
                  >
                    <ConcernRow label={CONCERN_LABELS.skin_darkcircle} value={draft.skin_darkcircle} onChange={(v) => { setDraft({ ...draft, skin_darkcircle: v }); }} />
                    <ConcernRow label={CONCERN_LABELS.skin_skinredness} value={draft.skin_skinredness} onChange={(v) => { setDraft({ ...draft, skin_skinredness: v }); }} />
                    <ConcernRow label={CONCERN_LABELS.skin_vascular}    value={draft.skin_vascular}    onChange={(v) => { setDraft({ ...draft, skin_vascular: v }); }} />
                    <ConcernRow label={CONCERN_LABELS.skin_wrinkle}     value={draft.skin_wrinkle}     onChange={(v) => { setDraft({ ...draft, skin_wrinkle: v }); }} />
                    <ConcernRow label={CONCERN_LABELS.skin_acne}        value={draft.skin_acne}        onChange={(v) => { setDraft({ ...draft, skin_acne: v }); }} />
                  </div>
                </div>

                <div className="result-editor__actions">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => {
                      setDraft({
                        job_id: id,
                        skin_oily: clamp01(job.skin_oily ?? 0),
                        skin_dry: clamp01(job.skin_dry ?? 0),
                        skin_normal: clamp01(job.skin_normal ?? 0),
                        skin_darkcircle: clamp01(job.skin_darkcircle ?? 0),
                        skin_skinredness: clamp01(job.skin_skinredness ?? 0),
                        skin_vascular: clamp01(job.skin_vascular ?? 0),
                        skin_wrinkle: clamp01(job.skin_wrinkle ?? 0),
                        skin_acne: clamp01(job.skin_acne ?? 0),
                      });
                    }}
                  >
                    Reset to AI reading
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    className="btn btn--ghost btn--sm"
                    onClick={() => { setOpenRefiner(false); }}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    className="btn btn--primary btn--sm"
                    onClick={() => {
                      void (async () => {
                        setSaving(true);
                        setSaveState('idle');
                        const payload: PostCorrectJobPayload = {
                          job_id: id,
                          skin_oily: clamp01(draft.skin_oily),
                          skin_dry: clamp01(draft.skin_dry),
                          skin_normal: clamp01(draft.skin_normal),
                          skin_darkcircle: clamp01(draft.skin_darkcircle),
                          skin_skinredness: clamp01(draft.skin_skinredness),
                          skin_vascular: clamp01(draft.skin_vascular),
                          skin_wrinkle: clamp01(draft.skin_wrinkle),
                          skin_acne: clamp01(draft.skin_acne),
                        };
                        try {
                          await postCorrectJob(payload);
                          const c: CorrectedJob = { ...payload };
                          setCorrected(c);
                          setSkin(mergeSkinWithCorrected(rawSkin, c));
                          const s2 = await getSkin(id);
                          setSkin(s2);
                          setProducts({});
                          setLoadingMap({});
                          setSaveState('ok');
                        } catch {
                          setSaveState('err');
                        } finally {
                          setSaving(false);
                        }
                      })();
                    }}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.section>

        {/* Probabilities card */}
        <motion.section
          variants={sectionIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '0px 0px -60px' }}
          className="card"
          style={{ padding: 'var(--s-5)' }}
        >
          <span className="t-label" style={{ display: 'block', marginBottom: 'var(--s-4)' }}>
            Appearance signals
          </span>

          <motion.div
            variants={listContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="stack-3"
          >
            {computedConcerns.map((row) => {
              const v = clamp01(row.value);
              if (v === 0) return null;
              const vGhost = clamp01(row.ghost);
              // Resolve colors/icon from the stable internal key, not the
              // user-facing label (labels no longer contain match substrings).
              const { dot, bar } = barVariant(row.key);
              const displayName = capFirst(row.name);
              const vv = pct(v);
              const vvGhost = pct(vGhost);

              return (
                <motion.div key={row.key} variants={listItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-3)' }}>
                    <div className="row-3" style={{ width: '8.5rem', flexShrink: 0, minWidth: 0 }}>
                      <div className={`concern-dot ${dot}`}>{iconForConcern(row.key)}</div>
                      <span
                        className="t-small"
                        style={{
                          // Wrap long condition names instead of clipping them
                          // with an ellipsis; the column width keeps bars aligned.
                          overflowWrap: 'anywhere',
                          lineHeight: 1.25,
                          color: 'var(--ink)',
                          fontWeight: 500,
                        }}
                      >
                        {displayName}
                      </span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        className={`result-bar${bar ? ` ${bar}` : ''}`}
                        role="progressbar"
                        aria-label={`${displayName} probability`}
                        aria-valuenow={vv}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div
                          className="result-bar__ghost"
                          style={{ width: `${vvGhost}%` }}
                          aria-hidden
                        />
                        <motion.div
                          className="result-bar__fill"
                          initial={{ width: 0 }}
                          whileInView={{ width: `${vv}%` }}
                          viewport={{ once: true, margin: '-15% 0px -15% 0px' }}
                          transition={spring}
                        />
                        <div className="result-bar__gloss" />
                        <div className="result-bar__label">
                          <span
                            className="t-small"
                            style={{ fontWeight: 600, color: 'var(--ink)' }}
                          >
                            {vv}%
                          </span>
                          <span className="t-small t-mute">· AI {vvGhost}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {corrected && (
            <motion.p
              variants={listItem}
              className="t-small t-mute"
              style={{
                margin: 0,
                marginTop: 'var(--s-4)',
                paddingTop: 'var(--s-3)',
                borderTop: '1px solid var(--rule)',
              }}
            >
              Displaying <span style={{ color: 'var(--ink)', fontWeight: 600 }}>adjusted</span>{' '}
              values; <span>baseline</span> shows the original AI output.
            </motion.p>
          )}

          {/* Skin type breakdown */}
          <div style={{ marginTop: 'var(--s-5)', paddingTop: 'var(--s-4)', borderTop: '1px solid var(--rule)' }}>
            <span className="t-label" style={{ display: 'block', marginBottom: 'var(--s-4)' }}>
              Skin type breakdown
            </span>
            <div className="stack-3">
              {(
                [
                  { label: 'Normal', key: 'skin_normal', bar: ''                  },
                  { label: 'Oily',   key: 'skin_oily',   bar: 'result-bar--coral' },
                  { label: 'Dry',    key: 'skin_dry',    bar: 'result-bar--deep'  },
                ] as const
              ).map(({ label, key, bar }) => {
                const v = pct(skinTypeBreakdownValue(corrected, job, key));
                return (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-3)' }}>
                    <span
                      className="t-small"
                      style={{ width: '3.5rem', flexShrink: 0, fontWeight: 500, color: 'var(--ink)' }}
                    >
                      {label}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div
                        className={`result-bar${bar ? ` ${bar}` : ''}`}
                        role="progressbar"
                        aria-label={`${label} ${v}%`}
                        aria-valuenow={v}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <motion.div
                          className="result-bar__fill"
                          initial={{ width: 0 }}
                          whileInView={{ width: `${v}%` }}
                          viewport={{ once: true, margin: '-15% 0px -15% 0px' }}
                          transition={spring}
                        />
                        <div className="result-bar__gloss" />
                        <div className="result-bar__label">
                          <span className="t-small" style={{ fontWeight: 600, color: 'var(--ink)' }}>
                            {v}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.section>
      </div>

      {/* ── Recommendations ────────────────────────────────── */}
      <motion.section
        variants={sectionIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="card"
        style={{ padding: 'var(--s-5)' }}
      >
        <span className="t-label" style={{ display: 'block', marginBottom: 'var(--s-2)' }}>
          Recommendations
        </span>
        <p className="t-small t-mute" style={{ margin: '0 0 var(--s-5)' }}>
          Cosmetic suggestions, not medical advice.
        </p>

        <div
          className="stack-6"
          style={{ fontSize: 'var(--fs-body)', lineHeight: 'var(--leading-body)' }}
        >
          {overviewText && (
            <section>
              <h4 className="t-h3" style={{ margin: '0 0 var(--s-2)' }}>Overview</h4>
              <motion.div layout style={{ position: 'relative' }}>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.p
                    key={expandedAdvice ? 'full' : 'trunc'}
                    initial={{ opacity: 0, y: 2 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -2 }}
                    transition={quick}
                    className="t-body t-mute"
                    style={{ margin: 0 }}
                  >
                    {expandedAdvice
                      ? linkifyText(overviewText)
                      : linkifyText(truncated(overviewText, 240))}
                  </motion.p>
                </AnimatePresence>
                {!expandedAdvice && overviewText.length > 240 && (
                  <div
                    style={{
                      pointerEvents: 'none',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '2rem',
                      background: 'linear-gradient(to top, var(--c-cream), transparent)',
                    }}
                  />
                )}
              </motion.div>
              {overviewText.length > 240 && (
                <motion.button
                  type="button"
                  whileTap={{ scale: prefersReduced ? 1 : 0.98 }}
                  style={{
                    marginTop: 'var(--s-3)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    font: `500 var(--fs-small)/1 var(--f-text)`,
                    color: 'var(--c-deep-teal)',
                    padding: 0,
                  }}
                  onClick={() => { setExpandedAdvice((s) => !s); }}
                >
                  {expandedAdvice ? 'Show less' : 'Read more'}
                </motion.button>
              )}
            </section>
          )}

          {skin.base_products.length > 0 && (
            <section>
              <h4 className="t-h3" style={{ margin: '0 0 var(--s-2)' }}>Core routine</h4>
              <p className="t-small t-mute" style={{ margin: '0 0 var(--s-2)' }}>
                Your daily foundation. Select any item to browse matching products.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)' }}>
                {skin.base_products.map((k) => (
                  <KeywordChip key={`base-${k}`} k={k} onOpen={openKeyword} />
                ))}
              </div>
            </section>
          )}

          {(() => {
            const concerns = skin.concerns.filter((c) => isSurfacedConcern(c.name));
            if (concerns.length === 0) return null;
            return (
              <section>
                <h4 className="t-h3" style={{ margin: '0 0 var(--s-3)' }}>Appearance areas to focus on</h4>
                <div className="stack-3">
                  {concerns.map((c) => {
                    const ck = keyForConcernName(c.name);
                    const displayName = ck ? concernLabel(ck) : capFirst(c.name);
                    const advice = c.advice ? c.advice.trim() : '';
                    const recs = c.recommended_products;
                    return (
                      <div
                        key={c.name}
                        className="card card--sand card--compact"
                        style={{ border: '1px solid var(--rule)' }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 'var(--s-3)',
                          }}
                        >
                          <div style={{ fontWeight: 500, color: 'var(--ink)' }}>{displayName}</div>
                          <span
                            style={{
                              font: `500 var(--fs-label)/1 var(--f-text)`,
                              letterSpacing: 'var(--tracking-label)',
                              textTransform: 'uppercase',
                              color: 'var(--ink-muted)',
                            }}
                          >
                            {pct(clamp01(c.confidence))}% confidence
                          </span>
                        </div>
                        {advice && (
                          <p className="t-small t-mute" style={{ margin: 'var(--s-2) 0 0' }}>
                            {linkifyText(advice)}
                          </p>
                        )}
                        {recs.length > 0 && (
                          <div
                            style={{
                              marginTop: 'var(--s-2)',
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 'var(--s-2)',
                            }}
                          >
                            {recs.map((k) => (
                              <KeywordChip key={`${c.name}-${k}`} k={k} onOpen={openKeyword} />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })()}
        </div>
      </motion.section>

      {/* ── Products ───────────────────────────────────────── */}
      <ProductsSection
        sectionIn={sectionIn}
        allKeywords={allKeywords}
        products={products}
        loadingMap={loadingMap}
        selectedKeyword={selectedKeyword}
        onOpen={openKeyword}
        onClose={() => { setSelectedKeyword(null); }}
      />

      <AIDisclaimerFooter />
    </motion.div>
  );
}

/* ── Helper components ──────────────────────────────────── */

type ProductsSectionProps = {
  sectionIn: Variants;
  allKeywords: string[];
  products: Record<string, ProductSearchResultItem[]>;
  loadingMap: Record<string, boolean>;
  selectedKeyword: string | null;
  onOpen: (keyword: string) => void;
  onClose: () => void;
};

function KeywordChip({ k, onOpen }: { k: string; onOpen: (k: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => { onOpen(k); }}
      className="pill pill--product"
      title={`Browse ${k} products`}
    >
      <span className="dot" />
      {k}
    </button>
  );
}

function getSkinProductIcon(t: string): string {
  const l = t.toLowerCase();
  if (l.includes('cleanser') || l.includes('wash') || l.includes('foam')) return 'soap';
  if (l.includes('moistur') || l.includes('cream') || l.includes('lotion')) return 'opacity';
  if (l.includes('serum') || l.includes('essence')) return 'science';
  if (l.includes('sunscreen') || l.includes('spf') || l.includes('sun')) return 'wb_sunny';
  if (l.includes('mask') || l.includes('treatment')) return 'spa';
  if (l.includes('oil')) return 'water_drop';
  if (l.includes('toner') || l.includes('mist')) return 'sanitizer';
  if (l.includes('exfoli') || l.includes('peel') || l.includes('acid') || l.includes('scrub')) return 'grain';
  if (l.includes('eye')) return 'visibility';
  if (l.includes('spot') || l.includes('acne') || l.includes('blemish')) return 'target';
  return 'health_and_beauty';
}

function ProductTypeCard({ type, onClick }: { type: string; onClick: () => void }) {
  return (
    <button className="product-type-card" onClick={onClick} type="button" aria-label={`Browse ${type} products`}>
      <div className="product-type-card__icon">
        <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>{getSkinProductIcon(type)}</span>
      </div>
      <div className="product-type-card__body">
        <p className="product-type-card__name">{type}</p>
        <p className="product-type-card__hint">Matched to your skin profile.</p>
      </div>
      <div className="product-type-card__arrow">
        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>arrow_forward</span>
      </div>
    </button>
  );
}

function ProductsSection(props: ProductsSectionProps) {
  const { sectionIn, allKeywords, products, loadingMap, selectedKeyword, onOpen, onClose } = props;
  const items = selectedKeyword ? (products[selectedKeyword] ?? []) : [];
  const loading = selectedKeyword ? loadingMap[selectedKeyword] : false;

  return (
    <motion.section
      variants={sectionIn}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="stack-4"
    >
      <span className="t-label">Products</span>
      <p className="t-small t-mute" style={{ marginTop: 0 }}>
        Suggestions matched to your skin — tap a category to browse real results.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--s-4)' }}>
        {allKeywords.map((k) => (
          <ProductTypeCard key={k} type={k} onClick={() => { onOpen(k); }} />
        ))}
      </div>

      {selectedKeyword && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div className="modal" style={{ zIndex: 110 }}>
          <div
            className="modal__backdrop"
            role="button"
            tabIndex={0}
            aria-label="Close dialog"
            onClick={onClose}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.18 }}
            className="modal__card modal__card--lg"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}
          >
            <div className="modal__header">
              <h3 className="modal__title" style={{ textTransform: 'capitalize' }}>{selectedKeyword}</h3>
              <button onClick={onClose} className="btn btn--icon" aria-label="Close" type="button">
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>close</span>
              </button>
            </div>
            <div className="modal__scroll">
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s-9)' }}>
                  <span className="material-symbols-outlined t-mute animate-spin" style={{ fontSize: '32px' }}>progress_activity</span>
                </div>
              ) : items.length > 0 ? (
                <div className="stack-3">
                  {items.map((product, idx) => (
                    <a
                      key={idx}
                      href={product.product_link}
                      target="_blank"
                      rel="noreferrer"
                      className="product-result"
                      onClick={() => { void productClick({ product_keyword: selectedKeyword, product_link: product.product_link }); }}
                    >
                      <div className="product-result__inner">
                        {product.product_img_url ? (
                          <img src={product.product_img_url} alt={product.product_name} className="product-result__img" style={{ objectFit: 'contain' }} />
                        ) : (
                          <div className="product-result__img">
                            <span className="material-symbols-outlined">image</span>
                          </div>
                        )}
                        <div className="product-result__body">
                          <p className="product-result__name">{product.product_name}</p>
                          {product.product_price != null && product.product_price !== '' && (
                            <p className="product-result__price">
                              {typeof product.product_price === 'number' ? `$${product.product_price}` : product.product_price}
                            </p>
                          )}
                          <p className="product-result__desc">{product.product_description ?? 'No description available.'}</p>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--s-9)', textAlign: 'center', gap: 'var(--s-4)' }}>
                  <div className="icon-ring">
                    <span className="material-symbols-outlined">inventory_2</span>
                  </div>
                  <p className="t-small t-mute">No products found for this category.</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>,
        document.body,
      )}
    </motion.section>
  );
}

function Segmented({
  options,
  value,
  onChange,
}: {
  options: { key: 'oily' | 'normal' | 'dry'; label: string }[];
  value: 'oily' | 'normal' | 'dry';
  onChange: (k: 'oily' | 'normal' | 'dry') => void;
}) {
  return (
    <div className="result-seg">
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => { onChange(opt.key); }}
          className={`result-seg__btn${opt.key === value ? ' is-active' : ''}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function SegmentedScale({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const options: { key: LevelKey; label: string; v: number }[] = [
    { key: 'none',     label: 'None', v: 0    },
    { key: 'mild',     label: 'Low',  v: 0.33 },
    { key: 'moderate', label: 'Med',  v: 0.66 },
    { key: 'high',     label: 'High', v: 1    },
  ];
  const activeKey = levelKeyFromValue(value);
  return (
    <div className="result-seg-scale">
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => { onChange(opt.v); }}
          className={`result-seg-scale__btn${opt.key === activeKey ? ' is-active' : ''}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ConcernRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="stack-2">
      <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>{label}</div>
      <SegmentedScale value={value} onChange={onChange} />
    </div>
  );
}
